import { debugLog, debugWarn } from '@/lib/debug'
import { APPLE_DEVICE_MODELS } from '@/lib/device-models'
import { getEventName, formatEventDetails } from '@/app/analytics/event-mappings'
import {
  type ScenarioReport,
  type FullScenarioReport,
  type AnalyticsEvent,
  type AnalyticsSession,
  type Scenario,
} from '@/lib/api-v3'
import { type ScenarioRun } from '@/app/scenarios/columns'
import { getPlatformFromDataJson, calcViewMinutes, getLastActivityTimestampMs } from '@/lib/report-utils'
import { csprTitleFromFileName } from '@/lib/cspr/library'
import { type EventRow } from '@/app/analytics/events-columns'
import { type SessionRow } from '@/app/analytics/sessions-columns'
import { type SessionEventRow } from '@/app/analytics/session-events-columns'
import { type CopilotRequestRow } from '@/app/analytics/copilot-requests-columns'

// ── Нормализаторы ответов API ──────────────────────────────────────────────

export function normalizeReportsPayload(payload: any): { items: ScenarioReport[]; total: number } {
  try {
    let items: ScenarioReport[] = []
    let total = 0
    if (Array.isArray(payload)) {
      items = payload as ScenarioReport[]
      total = items.length
    } else if (payload && Array.isArray(payload.result)) {
      items = payload.result as ScenarioReport[]
      total = payload.totalCount ?? items.length
    } else if (payload && Array.isArray(payload.items)) {
      items = payload.items as ScenarioReport[]
      total = payload.total ?? items.length
    } else if (payload && Array.isArray(payload.data)) {
      items = payload.data as ScenarioReport[]
      total = payload.total ?? items.length
    } else if (payload && payload.data && Array.isArray(payload.data.items)) {
      items = payload.data.items as ScenarioReport[]
      total = payload.data.total ?? payload.total ?? items.length
    } else if (payload && Array.isArray(payload.reports)) {
      items = payload.reports as ScenarioReport[]
      total = payload.total ?? items.length
    } else if (payload && Array.isArray(payload.rows)) {
      items = payload.rows as ScenarioReport[]
      total = payload.total ?? items.length
    } else if (payload && Array.isArray(payload.list)) {
      items = payload.list as ScenarioReport[]
      total = payload.total ?? items.length
    } else {
      debugWarn('api', 'normalizeReportsPayload: неизвестная форма ответа', payload)
    }
    return { items, total }
  } catch (e) {
    debugWarn('api', 'normalizeReportsPayload error:', e)
    return { items: [], total: 0 }
  }
}

export function normalizeEventsPayload(payload: any): { items: AnalyticsEvent[]; total: number } {
  try {
    let items: AnalyticsEvent[] = []
    let total = 0
    if (Array.isArray(payload)) {
      items = payload as AnalyticsEvent[]
      total = items.length
    } else if (payload && Array.isArray(payload.items)) {
      items = payload.items as AnalyticsEvent[]
      total = payload.total ?? items.length
    } else if (payload && Array.isArray(payload.data)) {
      items = payload.data as AnalyticsEvent[]
      total = payload.total ?? items.length
    } else if (payload && payload.data && Array.isArray(payload.data.items)) {
      items = payload.data.items as AnalyticsEvent[]
      total = payload.data.total ?? payload.total ?? items.length
    } else if (payload && Array.isArray(payload.result)) {
      items = payload.result as AnalyticsEvent[]
      total = payload.total ?? items.length
    } else if (payload && Array.isArray(payload.events)) {
      items = payload.events as AnalyticsEvent[]
      total = payload.total ?? items.length
    } else {
      debugWarn('api', 'normalizeEventsPayload: неизвестная форма ответа', payload)
    }
    return { items, total }
  } catch (e) {
    debugWarn('api', 'normalizeEventsPayload error:', e)
    return { items: [], total: 0 }
  }
}

export function normalizeSessionsPayload(payload: any): { items: AnalyticsSession[]; total: number } {
  try {
    let items: AnalyticsSession[] = []
    let total = 0
    if (Array.isArray(payload)) {
      items = payload as AnalyticsSession[]
      total = items.length
    } else if (payload && Array.isArray(payload.items)) {
      items = payload.items as AnalyticsSession[]
      total = payload.total ?? items.length
    } else if (payload && Array.isArray(payload.data)) {
      items = payload.data as AnalyticsSession[]
      total = payload.total ?? items.length
    } else if (payload && payload.data && Array.isArray(payload.data.items)) {
      items = payload.data.items as AnalyticsSession[]
      total = payload.data.total ?? payload.total ?? items.length
    } else if (payload && Array.isArray(payload.result)) {
      items = payload.result as AnalyticsSession[]
      total = payload.total ?? items.length
    } else if (payload && Array.isArray(payload.sessions)) {
      items = payload.sessions as AnalyticsSession[]
      total = payload.total ?? items.length
    } else {
      debugWarn('api', 'normalizeSessionsPayload: неизвестная форма ответа', payload)
    }
    return { items, total }
  } catch (e) {
    debugWarn('api', 'normalizeSessionsPayload error:', e)
    return { items: [], total: 0 }
  }
}

// ── Маппинг отчётов → строки таблицы ──────────────────────────────────────

export function mapReportsToRows(reports: ScenarioReport[]): ScenarioRun[] {
  return (reports || []).map((r) => {
    const start = new Date(r.startTime)
    const months = ['янв', 'фев', 'мар', 'апр', 'май', 'июн', 'июл', 'авг', 'сен', 'окт', 'ноя', 'дек']
    const dateStr = `${start.getDate()} ${months[start.getMonth()]} ${start.getFullYear()}`
    const timeStr = start.toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' })
    const anyR: any = r
    // Время прохождения: сумма длительностей шагов из timeline (исключает паузы),
    // затем totalTime с сервера (теперь тоже рассчитывается как сумма шагов),
    // и в крайнем случае — разница end - start.
    const minutes = calcViewMinutes({
      dataJson: anyR.dataJson ?? null,
      totalTime: anyR.totalTime ?? null,
      endTime: r.endTime ?? null,
      startTime: r.startTime ?? null,
    })

    let resultPercent: number | null = null
    try {
      const dataJson = anyR.dataJson
      if (dataJson && typeof dataJson === 'string') {
        const parsed = JSON.parse(dataJson)
        const steps = Array.isArray(parsed?.steps) ? parsed.steps : []
        let correctAnswers = 0
        let evaluatedQuestions = 0
        steps.forEach((step: any) => {
          if (step.formReport?.questions) {
            step.formReport.questions.forEach((q: any) => {
              const isSuccess = q.questionType === 4
                ? (typeof q.aiResult === 'boolean' ? q.aiResult : null)
                : (typeof q.isSuccess === 'boolean' ? q.isSuccess : null)
              // Считаем только вопросы с определённым результатом (не null)
              if (isSuccess !== null) {
                evaluatedQuestions++
                if (isSuccess === true) correctAnswers++
              }
            })
          }
        })
        if (evaluatedQuestions > 0) resultPercent = (correctAnswers / evaluatedQuestions) * 100
      }
    } catch {}

    let completedSteps = anyR.completedSteps != null ? Number(anyR.completedSteps) : undefined
    let totalSteps = anyR.totalSteps != null ? Number(anyR.totalSteps) : undefined

    if (completedSteps === undefined || totalSteps === undefined) {
      try {
        const dataJson = anyR.dataJson
        const parsed = typeof dataJson === 'string' ? JSON.parse(dataJson) : dataJson
        if (parsed && parsed.newFormat !== true && Array.isArray(parsed.steps)) {
          const steps: Array<{ isComplete?: boolean; formReport?: unknown; answer?: unknown }> = parsed.steps
          totalSteps = steps.length
          completedSteps = steps.filter(s => s.isComplete === true).length
          const stepsWithData = steps.filter(s => s.isComplete === true || s.formReport || s.answer)
          if (stepsWithData.length > 0 && stepsWithData[stepsWithData.length - 1].isComplete !== true) {
            completedSteps += 1
          }
        }
      } catch {}
    }

    const instructionIdRaw = anyR.instructionId ?? anyR.areaId ?? r.instructionId ?? (r as any).areaId
    const instructionId = instructionIdRaw != null ? Number(instructionIdRaw) : undefined
    const fileId = anyR.fileId != null ? Number(anyR.fileId) : undefined
    const scenarioIdRaw = anyR.scenarioId ?? r.scenarioId

    return {
      id: r.id,
      user: anyR.userName ? String(anyR.userName) : anyR.userLogin ? String(anyR.userLogin) : '—',
      userId: Number(r.userId),
      startDate: dateStr,
      startTime: timeStr,
      scenarioName: r.scenarioName || (anyR.fileName ? csprTitleFromFileName(String(anyR.fileName)) : '') || anyR.instructionName || '—',
      instructionName: r.areaName || anyR.areaName || anyR.instructionName || r.instructionName || '',
      viewMinutes: minutes,
      resultPercent,
      completedSteps,
      totalSteps,
      platform: getPlatformFromDataJson(anyR.dataJson ?? null),
      dataJson: anyR.dataJson ?? null,
      startTimestampSec: Math.round((anyR.startTime ?? r.startTime) / 1000),
      endTimestampSec: Math.round((anyR.endTime ?? r.endTime) / 1000),
      instructionId,
      scenarioId: scenarioIdRaw != null ? Number(scenarioIdRaw) : undefined,
      fileId,
      fileCommitId: anyR.fileCommitId != null ? Number(anyR.fileCommitId) : undefined,
      scenarioDeleted: anyR.scenarioDeleted === true || anyR.fileDeleted === true,
      areaDeleted: anyR.areaDeleted === true,
      // status=3 (Sent) → отчёт официально отправлен.
      // Если поле status присутствует и !== 3, отчёт ещё в работе — isCompleted=true
      // означает лишь «все шаги выполнены», а не «отправлен».
      // Fallback для старых workspace-отчётов без status и isCompleted:
      // если есть endTime > 0, значит отчёт был финализирован.
      isSubmitted:
        anyR.status === 3 ||
        (anyR.status == null && anyR.isCompleted === true) ||
        (anyR.status == null && anyR.isCompleted == null && (anyR.endTime ?? r.endTime ?? 0) > 0),
      reportStatus: anyR.status != null ? Number(anyR.status) : undefined,
      lastActivityTimestampMs: getLastActivityTimestampMs(anyR.dataJson ?? null) ?? undefined,
      progressPercent: (completedSteps !== undefined && totalSteps !== undefined && totalSteps > 0)
        ? Math.round((completedSteps / totalSteps) * 100)
        : null,
    }
  })
}

/**
 * Конвертирует вложенный в ScenarioAssignment.fullScenarioReport объект (тот же набор
 * полей, что и ScenarioReport) в ScenarioRun для <ReportDetailSheet /> — позволяет
 * открыть просмотр отчёта данными, которые уже пришли с назначением, без отдельного
 * запроса и независимо от того, завершено ли прохождение.
 */
export function mapFullReportToRun(report: FullScenarioReport): ScenarioRun {
  return mapReportsToRows([report as unknown as ScenarioReport])[0]
}

// ── Утилиты ────────────────────────────────────────────────────────────────

export function computeFormPercent(dataJson: string | undefined | null, scenario: Scenario | null): number | null {
  if (!scenario || !dataJson) return null
  try {
    // Собираем оцениваемые вопросы из снапшота; отдельно — обязательные
    const qualifyingQIds = new Set<number>()
    const requiredQIds = new Set<number>()
    scenario.steps
      ?.filter((s: any) => (s.objectState ?? 0) === 0)
      .forEach((step: any) => {
        step.forms
          ?.filter((f: any) => (f.objectState ?? 0) === 0)
          .forEach((form: any) => {
            form.questions
              ?.filter((q: any) => (q.objectState ?? 0) === 0)
              .forEach((q: any) => {
                const activeAnswers = q.answers?.filter((a: any) => (a.objectState ?? 0) === 0) ?? []
                if (q.hasRightAnswer === true || activeAnswers.some((a: any) => a.isRight === true)) {
                  qualifyingQIds.add(Number(q.id))
                  if (q.required === true) requiredQIds.add(Number(q.id))
                }
              })
          })
      })
    if (qualifyingQIds.size === 0) return null
    const parsed = JSON.parse(dataJson)
    let correctQ = 0
    let wrongQ = 0
    const answeredQIds = new Set<number>()
    const processFormReport = (formReport: any) => {
      if (!formReport?.questions) return
      formReport.questions.forEach((q: any) => {
        if (!qualifyingQIds.has(Number(q.questionId))) return
        answeredQIds.add(Number(q.questionId))
        const isSuccess = q.questionType === 4
          ? (typeof q.aiResult === 'boolean' ? q.aiResult : null)
          : (typeof q.isSuccess === 'boolean' ? q.isSuccess : null)
        // isSuccess: null — вопрос был отвечен, но без правильного варианта на момент отчёта; не учитываем
        if (isSuccess === true) correctQ++
        else if (isSuccess === false) wrongQ++
      })
    }
    if (parsed.newFormat === true && Array.isArray(parsed.timeline)) {
      parsed.timeline.forEach((s: any) => { if (s.formReport) processFormReport(s.formReport) })
    } else if (Array.isArray(parsed.steps)) {
      parsed.steps.forEach((s: any) => { if (s.formReport) processFormReport(s.formReport) })
    }
    // Обязательные вопросы, которые не попали в отчёт → тоже ошибка
    let unansweredRequired = 0
    for (const qId of requiredQIds) {
      if (!answeredQIds.has(qId)) unansweredRequired++
    }
    // Знаменатель = правильные + неправильные + необязательные пропущенные (согласован с errorCount)
    const total = correctQ + wrongQ + unansweredRequired
    if (total === 0) return null
    return Math.round((correctQ / total) * 100)
  } catch {
    return null
  }
}

export function parseDevice(userAgent: string | null): string {
  if (!userAgent) return '—'
  const deviceMatch = userAgent.match(/Device\/([^)]+)/)
  if (deviceMatch && deviceMatch[1]) {
    const deviceModel = deviceMatch[1].trim()
    if (APPLE_DEVICE_MODELS[deviceModel]) return APPLE_DEVICE_MODELS[deviceModel]
    return deviceModel
  }
  if (userAgent.includes('Android OS')) {
    const osMatch = userAgent.match(/Android OS (\d+)/)
    return osMatch ? `Android ${osMatch[1]}` : 'Android'
  }
  if (userAgent.includes('iOS')) {
    const iosMatch = userAgent.match(/iOS ([\d.]+)/)
    return iosMatch ? `iOS ${iosMatch[1]}` : 'iOS'
  }
  return 'Неизвестно'
}

export function formatDuration(beginTime: number, endTime: number): string {
  const durationMs = endTime - beginTime
  const seconds = Math.floor(durationMs / 1000)
  const minutes = Math.floor(seconds / 60)
  const hours = Math.floor(minutes / 60)
  const remainingMinutes = minutes % 60
  const remainingSeconds = seconds % 60
  if (hours > 0) {
    if (remainingMinutes === 0) return `${hours} ч`
    return `${hours} ч ${remainingMinutes} мин`
  }
  if (minutes > 0) {
    if (remainingSeconds === 0) return `${minutes} мин`
    return `${minutes} мин ${remainingSeconds} с`
  }
  return `${remainingSeconds} с`
}

export function formatTimeFromStart(elapsedMs: number): string {
  const totalSeconds = Math.floor(elapsedMs / 1000)
  const milliseconds = elapsedMs % 1000
  const hours = Math.floor(totalSeconds / 3600)
  const minutes = Math.floor((totalSeconds % 3600) / 60)
  const seconds = totalSeconds % 60
  const parts: string[] = []
  if (hours > 0) parts.push(`${hours}ч`)
  if (minutes > 0) parts.push(`${minutes}мин`)
  parts.push(`${seconds}s`)
  parts.push(`${milliseconds}мс`)
  return parts.join(' ')
}

export function mapSessionsToRows(sessions: AnalyticsSession[]): SessionRow[] {
  return (sessions || []).map((s) => {
    const sessionTime = new Date(s.beginTime)
    const currentYear = new Date().getFullYear()
    const sessionYear = sessionTime.getFullYear()
    const day = String(sessionTime.getDate()).padStart(2, '0')
    const month = String(sessionTime.getMonth() + 1).padStart(2, '0')
    const hour = String(sessionTime.getHours()).padStart(2, '0')
    const minute = String(sessionTime.getMinutes()).padStart(2, '0')
    let dateTime: string
    if (sessionYear === currentYear) {
      dateTime = `${day}.${month} ${hour}:${minute}`
    } else {
      const year = String(sessionYear).slice(-2)
      dateTime = `${day}.${month}.${year} ${hour}:${minute}`
    }
    return {
      id: s.id,
      user: (s as any).userName || s.userLogin || '—',
      dateTime,
      duration: formatDuration(s.beginTime, s.endTime),
      device: parseDevice(s.userAgent),
      userAgent: s.userAgent || '—',
      userId: s.userId,
      userLogin: s.userLogin,
      groupId: s.groupId,
      deviceId: s.deviceId,
      platform: s.platform,
      timeZone: s.timeZone,
      beginTime: s.beginTime,
      endTime: s.endTime,
      isClosed: s.isClosed,
      isPaused: s.isPaused,
    }
  })
}

export function mapEventsToRows(
  events: AnalyticsEvent[],
  viewMode: 'summary' | 'detailed' = 'detailed',
  usersMap: Map<number, { name: string; login: string }> = new Map(),
): EventRow[] {
  return (events || []).map((e) => {
    const eventTime = new Date(e.time)
    const currentYear = new Date().getFullYear()
    const eventYear = eventTime.getFullYear()
    const day = String(eventTime.getDate()).padStart(2, '0')
    const month = String(eventTime.getMonth() + 1).padStart(2, '0')
    const hour = String(eventTime.getHours()).padStart(2, '0')
    const minute = String(eventTime.getMinutes()).padStart(2, '0')
    let dateTime: string
    if (eventYear === currentYear) {
      dateTime = `${day}.${month} ${hour}:${minute}`
    } else {
      const year = String(eventYear).slice(-2)
      dateTime = `${day}.${month}.${year} ${hour}:${minute}`
    }
    const eventCode = e.name || `Событие типа ${e.type}`
    let eventName = getEventName(eventCode)
    const details = formatEventDetails(eventCode, e.value)
    const userInfo = usersMap.get(e.userId)
    const userName = userInfo?.name || e.userLogin || '—'
    const userLogin = userInfo?.login || e.userLogin || userName

    if (viewMode === 'summary') {
      let scenarioName = ''
      let valueData: any = e.value
      if (typeof e.value === 'string' && e.value.trim() !== '') {
        try { valueData = JSON.parse(e.value) } catch {}
      }
      if (valueData && typeof valueData === 'object') {
        scenarioName = valueData.name || valueData.scenarioName || valueData.title || ''
      }
      if (!scenarioName && (eventCode === 'scenario_completed' || eventCode === 'run_scenario')) {
        debugLog('api', '[Events] Не найдено название для события:', eventCode, 'value:', e.value)
      }
      switch (eventCode) {
        case 'scenario_completed':
          eventName = scenarioName ? `Прошел(ла) процедуру "${scenarioName}"` : 'Прошел(ла) процедуру'; break
        case 'run_scenario':
          eventName = scenarioName ? `Запустил(а) процедуру "${scenarioName}"` : 'Запустил(а) процедуру'; break
        case 'copilot_message_sent':
          eventName = 'Обратился(ась) к Copilot'; break
        case 'scenario_created':
          eventName = scenarioName ? `Создал(а) процедуру "${scenarioName}"` : 'Создал(а) процедуру'; break
        case 'scenario_deleted':
          eventName = scenarioName ? `Удалил(а) процедуру "${scenarioName}"` : 'Удалил(а) процедуру'; break
        case 'scenario_edit_opened':
          eventName = scenarioName ? `Открыл(а) редактор процедуры "${scenarioName}"` : 'Открыл(а) редактор процедуры'; break
        case 'new_object_created':
          const objectName = scenarioName || (valueData?.description ? valueData.description : '')
          eventName = objectName ? `Создал(а) объект "${objectName}"` : 'Создал(а) объект'; break
        case 'document_added':
          const addedFileName = valueData?.fileName || ''
          eventName = addedFileName ? `Добавил(а) документ "${addedFileName}"` : 'Добавил(а) документ'; break
        case 'document_deleted':
          const deletedFileName = valueData?.fileName || ''
          eventName = deletedFileName ? `Удалил(а) документ "${deletedFileName}"` : 'Удалил(а) документ'; break
      }
    }

    return {
      id: e.id,
      user: userName,
      dateTime,
      eventCode,
      eventName,
      details,
      userId: e.userId,
      userLogin,
      name: e.name,
      time: e.time,
      timeZone: e.timeZone,
      type: e.type,
      value: e.value,
      groupId: e.groupId,
      deviceId: e.deviceId,
      platform: e.platform,
      userAgent: e.userAgent,
      sessionId: e.sessionId,
    }
  })
}

export function mapCopilotEventsToRows(events: AnalyticsEvent[]): CopilotRequestRow[] {
  const rows: CopilotRequestRow[] = []
  for (let i = 0; i < events.length - 1; i++) {
    const currentEvent = events[i]
    const nextEvent = events[i + 1]
    let requestEvent: AnalyticsEvent | null = null
    let responseEvent: AnalyticsEvent | null = null
    if (currentEvent.name === 'copilot_message_sent' && nextEvent.name === 'copilot_response_received') {
      requestEvent = currentEvent
      responseEvent = nextEvent
    } else if (currentEvent.name === 'copilot_response_received' && nextEvent.name === 'copilot_message_sent') {
      requestEvent = nextEvent
      responseEvent = currentEvent
    }
    if (requestEvent && responseEvent) {
      const timeDiff = Math.abs(responseEvent.time - requestEvent.time)
      if (timeDiff <= 120000) {
        const eventTime = new Date(requestEvent.time)
        const currentYear = new Date().getFullYear()
        const eventYear = eventTime.getFullYear()
        const day = String(eventTime.getDate()).padStart(2, '0')
        const month = String(eventTime.getMonth() + 1).padStart(2, '0')
        const hour = String(eventTime.getHours()).padStart(2, '0')
        const minute = String(eventTime.getMinutes()).padStart(2, '0')
        let dateTime: string
        if (eventYear === currentYear) {
          dateTime = `${day}.${month} ${hour}:${minute}`
        } else {
          const year = String(eventYear).slice(-2)
          dateTime = `${day}.${month}.${year} ${hour}:${minute}`
        }
        rows.push({
          id: requestEvent.id,
          request: String(requestEvent.value || '—'),
          response: String(responseEvent.value || '—'),
          documents: '—',
          user: (requestEvent as any).userName || requestEvent.userLogin || '—',
          dateTime,
          userId: requestEvent.userId,
          userLogin: requestEvent.userLogin || null,
          requestTime: requestEvent.time,
          responseTime: responseEvent.time,
          requestId: requestEvent.id,
          responseId: responseEvent.id,
        })
        i++
      }
    }
  }
  return rows
}

export function mapSessionEventsToRows(events: AnalyticsEvent[], sessionBeginTime: number): SessionEventRow[] {
  return (events || []).map((e, index) => {
    const elapsedMs = e.time - sessionBeginTime
    const timeFromStart = formatTimeFromStart(elapsedMs)
    let timeBetween = '—'
    if (index < events.length - 1) {
      const nextEvent = events[index + 1]
      const betweenMs = Math.abs(nextEvent.time - e.time)
      timeBetween = formatTimeFromStart(betweenMs)
    }
    const eventCode = e.name || `Событие типа ${e.type}`
    const eventName = getEventName(eventCode)
    const details = formatEventDetails(eventCode, e.value)
    return {
      id: e.id,
      timeFromStart,
      timeBetween,
      eventCode,
      eventName,
      details,
      userId: e.userId,
      userLogin: e.userLogin,
      name: e.name,
      time: e.time,
      timeZone: e.timeZone,
      type: e.type,
      value: e.value,
      groupId: e.groupId,
      deviceId: e.deviceId,
      platform: e.platform,
      userAgent: e.userAgent,
      sessionId: e.sessionId,
    }
  })
}
